<template>
    <div>
        <v-card-subtitle class="font-italic" v-if="risk.updated_at">Zuletzt bearbeitet am {{risk.updated_at}}</v-card-subtitle>
        <v-card-title class="subtitle-2">
            Hinweis: Nur für Mitarbeiter ab TG 6
            <v-spacer/>
            <v-dialog v-model="helpDialog" width="800">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                            small
                            outlined
                            v-on="on"
                            v-bind="attrs"
                    >
                        Hilfe<v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                </template>
                <v-card>
                    <v-card-title>
                        Beurteilung Personalrisiken
                    </v-card-title>
                    <v-card-text>
                        <p>Diese Beurteilung führen wir aus praktischen Überlegungen zeitgleich mit dem Mitarbeitergespräch durch.</p>
                        Allerdings ist die Beurteilung der Personalrisiken <span class="font-weight-bold">nicht</span> Gegenstand des Mitarbeitergesprächs,
                            sondern <span class="font-weight-bold">alleinige Einschätzung durch die Führungskraft.</span>
                    </v-card-text>
                    <v-card-text>
                        Die Risikoausprägung und die Risikofolgen beurteilen Sie bitte nach der folgenden Skala:
                        <v-simple-table>
                            <template v-slot:default>
                                <thead>
                                <tr>
                                    <th></th>
                                    <th>Ausprägung Risikokategorie</th>
                                    <th>Risikofolgen bei Risikoeintritt</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td>
                                        <v-avatar color="red" size="30">5</v-avatar>
                                    </td>
                                    <td>sehr hohes Risiko</td>
                                    <td>erheblich</td>
                                </tr>
                                <tr>
                                    <td>
                                        <v-avatar color="orange" size="30">4</v-avatar>
                                    </td>
                                    <td>hohes Risiko</td>
                                    <td>deutlich</td>
                                </tr>
                                <tr>
                                    <td>
                                        <v-avatar color="yellow" size="30">3</v-avatar>
                                    </td>
                                    <td>normales Risiko</td>
                                    <td>mittel</td>
                                </tr>
                                <tr>
                                    <td>
                                        <v-avatar color="green" size="30">2</v-avatar>
                                    </td>
                                    <td>geringes Risiko</td>
                                    <td>gering</td>
                                </tr>
                                <tr>
                                    <td>
                                        <v-avatar color="blue" size="30">1</v-avatar>
                                    </td>
                                    <td>sehr geringes Risiko</td>
                                    <td>nicht nennenswert</td>
                                </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card-text>

                    <v-card-text>
                        <p>
                            Da nicht jedes Risiko ein echtes Problem darstellen muss, ist für uns Ihre Einschätzung der
                            Risikofolgen ganz wesentlich.
                        </p>

                        Bilden Sie dabei pro Risikokategorie ein Gesamtergebnis. Die darunter stehenden
                        Teilfragen sollen Ihnen dabei helfen, hier zu einer nachvollziehbaren Gesamtwertung zu
                        bekommen. Sie müssen nicht/nicht vollständig beantwortet werden.
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn depressed color="success" @click="helpDialog = false">Okay</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card-title>
        <v-card-title>Qualifikationsrisiko</v-card-title>
        <v-card-text>
            <div class="subtitle-1">Deutliche Über-/Unterforderung und keine kurzfr. Anpassungsmöglichkeit</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.qualification_demands)"
                      v-model="risk.qualification_demands"
            > </v-slider>

            <div class="subtitle-1">Aufgabenstellung ist von überdurchschnittl. Wandel betroffen</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.qualification_tasks)"
                      v-model="risk.qualification_tasks"
            > </v-slider>

            <div class="subtitle-1">Zu geringe Fähigkeit oder Bereitschaft zur Veränderung</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.qualification_change)"
                      v-model="risk.qualification_change"
            > </v-slider>

            <div class="subtitle-1">Risiko, die Potenziale des Mitarbeiters nicht zu erkennen/nutzen</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.qualification_potential)"
                      v-model="risk.qualification_potential"
            > </v-slider>

            <v-textarea :readonly="readonly" outlined label="Anmerkungen" v-model="risk.qualification_notes"/>
        </v-card-text>

        <v-card-title>Engpassrisiko</v-card-title>
        <v-card-text>
            <div class="subtitle-1">Begrenzte Einsatzmöglichkeiten aufgrund Krankheit/bes. Ereignisse</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.bottleneck_use)"
                      v-model="risk.bottleneck_use"
            > </v-slider>

            <div class="subtitle-1">Nachfolgeproblem bei bevorstehendem Austritt (Ruhestand)</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.bottleneck_exit)"
                      v-model="risk.bottleneck_exit"
            > </v-slider>

            <div class="subtitle-1">Aufgabenkonzentration (Abhängigkeit vom Mitarbeiter)</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.bottleneck_tasks)"
                      v-model="risk.bottleneck_tasks"
            > </v-slider>

            <div class="subtitle-1">Besondere persönliche/familiäre Situation mit negativer Auswirkung</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.bottleneck_personal)"
                      v-model="risk.bottleneck_personal"
            > </v-slider>

            <v-textarea :readonly="readonly" outlined label="Anmerkungen" v-model="risk.bottleneck_notes"/>
        </v-card-text>

        <v-card-title>Motivatíons-/Leistungsrisiko</v-card-title>
        <v-card-text>
            <div class="subtitle-1">Unzufriedenheit (Aufgabe, Umfeld, „innere Kündigung“)</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.motivation_unhappy)"
                      v-model="risk.motivation_unhappy"
            > </v-slider>

            <div class="subtitle-1">Minderleistung im Vergleich zum Durchschnitt und zur Vergütung</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.motivation_compare)"
                      v-model="risk.motivation_compare"
            > </v-slider>

            <div class="subtitle-1">Vermisst Förderung (Vergütung und Entwicklung)</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.motivation_advancement)"
                      v-model="risk.motivation_advancement"
            > </v-slider>

            <div class="subtitle-1">Persönliche Belastungsfaktoren</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.motivation_load)"
                      v-model="risk.motivation_load"
            > </v-slider>

            <v-textarea :readonly="readonly" outlined label="Anmerkungen" v-model="risk.motivation_notes"/>
        </v-card-text>

        <v-card-title>Austrittsrisiko</v-card-title>
        <v-card-text>
            <div class="subtitle-1">Vermisst Perspektive bzw. Bank kann ihm keine bieten</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.exit_perspective)"
                      v-model="risk.exit_perspective"
            > </v-slider>

            <div class="subtitle-1">Abwerbegefahr (hohe Marktattraktivität aufgrund Kompetenz/Wissen)</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.exit_headhunt)"
                      v-model="risk.exit_headhunt"
            > </v-slider>

            <div class="subtitle-1">Unzufriedenheit mit Vergütung ist feststellbar</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.exit_money)"
                      v-model="risk.exit_money"
            > </v-slider>

            <div class="subtitle-1">Fühlt sich nicht wohl (Bank/Bereich/Team/Regionalmarkt)</div>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.exit_unhappy)"
                      v-model="risk.exit_unhappy"
            > </v-slider>

            <v-textarea :readonly="readonly" outlined label="Anmerkungen" v-model="risk.exit_notes"/>
        </v-card-text>

        <v-card-title>Folgen bei Risikoeintritt</v-card-title>
        <v-card-text>
            <v-slider :readonly="readonly" class="mt-12 mb-6" ticks="always" tick-size="5" min="0" max="5" :tick-labels="['Keine Angabe',1,2,3,4,5]" thumb-label="always"
                      :thumb-color="thumbColor(risk.effects)"
                      v-model="risk.effects"
            > </v-slider>

            <v-textarea outlined label="Anmerkungen" v-model="risk.effects_notes"/>
        </v-card-text>
    </div>
</template>

<script>
    import {mapActions} from 'vuex';

    export default {
        props: ['readonly', 'payload'],
        name: "",
        data: () => ({
            helpDialog: false,
            risk: {
                id: '',
                updated_at: '',
                is_released: false,
                qualification_demands: 0,
                qualification_tasks: 0,
                qualification_change: 0,
                qualification_potential: 0,
                qualification_notes: '',

                bottleneck_use: 0,
                bottleneck_exit: 0,
                bottleneck_tasks: 0,
                bottleneck_personal: 0,
                bottleneck_notes: '',

                motivation_unhappy: 0,
                motivation_compare: 0,
                motivation_advancement: 0,
                motivation_load: 0,
                motivation_notes: '',

                exit_perspective: 0,
                exit_headhunt: 0,
                exit_money: 0,
                exit_unhappy: 0,
                exit_notes: '',

                effects: 0,
                effects_notes: '',
            },
        }),
        methods: {
            ...mapActions({}),
            thumbColor(val){
                var color = 'grey lighten-1';

                switch (val) {
                    case 1:
                        color = 'blue';
                        break;
                    case 2:
                        color = 'green';
                        break;
                    case 3:
                        color = 'yellow';
                        break;
                    case 4:
                        color = 'orange';
                        break;
                    case 5:
                        color = 'red';
                        break;
                }
                return color;
            }

        },
        mounted() {
            if (this.payload.id){
                this.risk = this.payload;
            }
            else {
                this.$emit('pass', this.risk);
            }
        },
        watch: {
            payload: function(val){
                if (val.id){
                    this.risk = val;
                }
            },
            risk: function (val) {
                if (val.id){
                    this.$emit('pass', val);
                }
            }
        },
    }
</script>

<style scoped>

</style>